import {
  ALGOLIA_HAS_LISTABLE_STOCK_FILTER,
  DEFAULT_WAREHOUSE_ID,
} from 'commons/constants';
import { logInfo } from 'commons/logger';
import { buildInStockWarehouseFilterClause } from './buildInStockWarehouseFilterClause';
import {
  getAlgoliaCategoryFilterSlug,
  getAlgoliaFacetAttribute,
} from './categoryPageSlugUtils';
import { PAGE_TYPE, getCategoryPageTypeConfig } from './categoryPageTypes';

const escapeFilterValue = value => `"${String(value).replace(/"/g, '\\"')}"`;

/**
 * Builds the Algolia `filters` string for the category page.
 *
 * These base constraints (category, warehouse, ready-to-ship, sidebar facets)
 * must live in `filters` rather than `Configure.facetFilters`. When a color is
 * selected the `option_color` refinementList widget generates its own
 * `facetFilters`, which fully overrides any `facetFilters` set via Configure
 * (dropping the category). The `filters` string is an independent parameter, so
 * it merges cleanly with the color refinement and is also applied to the
 * disjunctive facet-count query — meaning a warehouse clause narrows facet
 * counts too.
 *
 * Contract: a warehouse-scoped page type always emits a warehouse clause. A
 * missing `warehouseId` falls back to `DEFAULT_WAREHOUSE_ID` rather than
 * dropping the clause, which would return the whole catalogue. The sidebar
 * Ready-to-Ship toggle is different: without a zip it cannot know a warehouse
 * and uses `is_in_stock:true`; with a zip it unions that warehouse with `154`.
 *
 * New Arrivals 90-day window is numeric (`go_live_date_unix`) and lives in
 * `numericFilters`, not here. Do not add `categories:"new-arrivals"`.
 */
const buildCategoryAlgoliaConfigureFilters = ({
  slug,
  isReadyToShipActive,
  selectedFacets,
  warehouseId,
  hasUserZip = false,
  pageType = PAGE_TYPE.CATEGORY,
}) => {
  const parts = [ALGOLIA_HAS_LISTABLE_STOCK_FILTER];

  const {
    usesCategorySlugFilter,
    variantWarehouseField,
    showReadyToShipToggle,
  } = getCategoryPageTypeConfig(pageType);

  const algoliaCategorySlug = usesCategorySlugFilter
    ? getAlgoliaCategoryFilterSlug(slug)
    : null;

  if (algoliaCategorySlug) {
    parts.push(`categories:${escapeFilterValue(algoliaCategorySlug)}`);
  }

  const pushWarehouseClause = field => {
    if (!warehouseId) {
      logInfo(
        `buildCategoryAlgoliaConfigureFilters: no warehouseId for page type "${pageType}", falling back to ${DEFAULT_WAREHOUSE_ID}`
      );
    }

    // Unquoted on purpose: in Algolia `attr:154` (numeric) and `attr:"154"`
    // (string) are not equivalent.
    parts.push(
      `product_variants.${field}:${warehouseId || DEFAULT_WAREHOUSE_ID}`
    );
  };

  if (variantWarehouseField) {
    pushWarehouseClause(variantWarehouseField);
  }

  // The toggle matches cart/PDP: zip warehouse OR default DC. A page type
  // that hides it (ready-to-ship, where the warehouse clause already is the
  // stock filter) never emits a second clause.
  if (isReadyToShipActive && showReadyToShipToggle) {
    parts.push(
      buildInStockWarehouseFilterClause({
        warehouseId,
        hasUserZip,
        field: getCategoryPageTypeConfig(PAGE_TYPE.READY_TO_SHIP)
          .variantWarehouseField,
      })
    );
  }

  Object.entries(selectedFacets || {}).forEach(([attr, values]) => {
    if (values?.size > 0) {
      const algoliaAttribute = getAlgoliaFacetAttribute(attr);
      const valueFilters = [...values].map(
        value => `${algoliaAttribute}:${escapeFilterValue(value)}`
      );
      parts.push(
        valueFilters.length === 1
          ? valueFilters[0]
          : `(${valueFilters.join(' OR ')})`
      );
    }
  });

  return parts.join(' AND ');
};

export default buildCategoryAlgoliaConfigureFilters;
